import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { Reveal } from "./Reveal";
import { API, SERVICE_LABEL, formatErr, waLink } from "@/lib/site";

const field = "w-full bg-transparent border-0 border-b border-[#121518]/20 focus:border-[#0F4C3A] focus:outline-none py-3 text-base text-[#121518] placeholder:text-[#525860]/70 transition-colors";
const lbl = "font-mono-d text-[11px] tracking-[0.2em] text-[#0F4C3A] uppercase";
const OPT = "(opcional)";

const SERVICES = [["comercial", "Comercial"], ["residencial", "Residencial"], ["pos_obra", "Pós-obra"]];
const FREQ = {
  comercial: ["1x por semana", "2 a 3x por semana", "Diária (dias úteis)", "Outra / a combinar"],
  residencial: ["Única", "Semanal", "Quinzenal", "Mensal", "A combinar"],
  pos_obra: [],
};
const PROPERTY = { residencial: ["Casa", "Apartamento"], pos_obra: ["Casa", "Apartamento", "Sala comercial", "Outro"] };
const ESTAB = ["Escritório", "Consultório", "Clínica de estética", "Sala comercial", "Imobiliária", "Escritório de advocacia", "Contabilidade", "Outro"];

const Input = ({ label, k, f, set, required, testid, ...p }) => (
  <label className="block"><span className={lbl}>{label}{!required && <span className="normal-case tracking-normal text-[#525860] ml-1">{OPT}</span>}</span>
    <input required={required} data-testid={testid} className={field} value={f[k]} onChange={set(k)} {...p} /></label>
);

const Select = ({ label, k, f, set, options, testid }) => (
  <label className="block"><span className={lbl}>{label}<span className="normal-case tracking-normal text-[#525860] ml-1">{OPT}</span></span>
    <select data-testid={testid} className={field} value={f[k]} onChange={set(k)}>
      <option value="">Não sei / selecionar</option>
      {options.map((o) => { const [v, t] = Array.isArray(o) ? o : [o, o]; return <option key={v} value={v}>{t}</option>; })}
    </select></label>
);

const ServiceToggle = ({ value, onChange }) => (
  <div className="grid grid-cols-3 gap-1 p-1 bg-[#F3F0EA] rounded-full" data-testid="select-service">
    {SERVICES.map(([v, t]) => (
      <button type="button" key={v} data-testid={`select-service-${v}`} onClick={() => onChange(v)} className="relative rounded-full py-3 text-xs sm:text-sm font-medium">
        {value === v && <motion.span layoutId="svc-pill" className="absolute inset-0 rounded-full bg-[#0F4C3A]" transition={{ type: "spring", stiffness: 300, damping: 30 }} />}
        <span className={`relative ${value === v ? "text-[#FAF8F5]" : "text-[#525860]"}`}>{t}</span>
      </button>
    ))}
  </div>
);

const CommercialFields = ({ f, set, visit, setVisit }) => (
  <>
    <div className="grid sm:grid-cols-2 gap-8">
      <Input label="Empresa" k="company" f={f} set={set} testid="input-company" placeholder="Nome da empresa" />
      <Select label="Tipo de estabelecimento" k="establishment_type" f={f} set={set} options={ESTAB} testid="select-establishment" />
      <Input label="Número de salas" k="rooms" f={f} set={set} testid="input-rooms" placeholder="Ex: 5" />
      <Input label="Tamanho aproximado" k="size" f={f} set={set} testid="input-size" placeholder="Ex: 150 m²" />
      <Select label="Frequência pretendida" k="frequency" f={f} set={set} options={FREQ.comercial} testid="select-frequency" />
      <Select label="Preferência de horário" k="time_preference" f={f} set={set} options={["Antes do expediente", "Durante o expediente", "Depois do expediente", "A combinar"]} testid="select-time" />
      <Select label="Interesse em contrato" k="contract_interest" f={f} set={set} options={["Mensal", "Anual", "Ainda não sei"]} testid="select-contract" />
    </div>
    <label className="flex items-center gap-3 cursor-pointer text-sm text-[#121518]">
      <input type="checkbox" data-testid="checkbox-visit" checked={visit} onChange={(e) => setVisit(e.target.checked)} className="w-5 h-5 accent-[#0F4C3A]" />
      Quero agendar uma visita técnica
    </label>
  </>
);

const OtherFields = ({ service, f, set }) => (
  <div className="grid sm:grid-cols-2 gap-8">
    <Select label="Tipo de imóvel" k="property_type" f={f} set={set} options={PROPERTY[service]} testid="select-property" />
    {service === "pos_obra"
      ? <Input label="Metragem (m²)" k="area_m2" f={f} set={set} type="number" min="1" testid="input-sqm" placeholder="Ex: 120" />
      : <Input label="Tamanho aproximado" k="size" f={f} set={set} testid="input-size" placeholder="Ex: 3 quartos, 90 m²" />}
    {service === "pos_obra"
      ? <Select label="Nível de sujeira" k="dirt_level" f={f} set={set} options={[["leve", "Leve"], ["media", "Média"], ["pesada", "Pesada"]]} testid="select-dirt-level" />
      : <Select label="Frequência desejada" k="frequency" f={f} set={set} options={FREQ.residencial} testid="select-frequency" />}
  </div>
);

const empty = { name: "", phone: "", location: "", property_type: "", size: "", area_m2: "", dirt_level: "", frequency: "", company: "", establishment_type: "", rooms: "", time_preference: "", contract_interest: "", message: "" };

const buildBody = (f, service, visit) => {
  const body = { service, visit_request: service === "comercial" && visit };
  Object.entries(f).forEach(([k, v]) => { body[k] = v === "" ? null : v; });
  body.area_m2 = service === "pos_obra" && f.area_m2 ? Number(f.area_m2) : null;
  if (service !== "pos_obra") body.dirt_level = null;
  if (service !== "comercial") ["company", "establishment_type", "rooms", "time_preference", "contract_interest"].forEach((k) => { body[k] = null; });
  else body.property_type = null;
  body.message = f.message || "";
  return body;
};

const Success = ({ sent, onNew }) => {
  const text = `Olá! Sou ${sent.name}${sent.company ? `, da ${sent.company}` : ""}. Enviei um pedido de orçamento de ${SERVICE_LABEL[sent.service].toLowerCase()} pelo site (${sent.location}).${sent.visit_request ? " Gostaria de agendar uma visita técnica." : ""}`;
  return (
    <div data-testid="quote-success" className="bg-[#0F4C3A] text-[#FAF8F5] p-8 md:p-12 grain relative">
      <p className="font-mono-d text-[11px] tracking-[0.3em] text-[#C59B27]">PEDIDO RECEBIDO</p>
      <h3 className="font-serif-d text-4xl md:text-5xl mt-4">Obrigado, {sent.name.split(" ")[0]}!</h3>
      <p className="mt-4 text-[#FAF8F5]/80">Recebemos seu pedido de {SERVICE_LABEL[sent.service].toLowerCase()}. Se quiser agilizar, envie também pelo WhatsApp.</p>
      <div className="mt-8 flex flex-wrap gap-3">
        <a data-testid="quote-success-whatsapp" href={waLink(text)} target="_blank" rel="noreferrer" className="rounded-full bg-[#C59B27] text-[#121518] px-7 py-3 text-sm font-medium hover:bg-[#FAF8F5] transition-colors">Enviar no WhatsApp</a>
        <button data-testid="quote-new" onClick={onNew} className="rounded-full border border-[#FAF8F5]/30 px-7 py-3 text-sm hover:border-[#FAF8F5] transition-colors">Novo pedido</button>
      </div>
    </div>
  );
};

export const QuoteRequest = ({ service, setService, visit, setVisit }) => {
  const [f, setF] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const body = buildBody(f, service, visit);
      await axios.post(`${API}/quotes`, body);
      setSent(body);
      setF(empty);
      toast.success("Pedido enviado! Entraremos em contato em breve.");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Não foi possível enviar.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="orcamento" className="py-24 md:py-36" data-testid="section-orcamento">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-14">
        <Reveal className="lg:col-span-5">
          <p className="font-mono-d text-[11px] tracking-[0.3em] text-[#0F4C3A] mb-5">ORÇAMENTO</p>
          <h2 className="font-serif-d text-5xl sm:text-6xl lg:text-7xl font-medium leading-[0.95] tracking-tight">
            Solicite uma <em className="text-[#0F4C3A]">proposta personalizada.</em>
          </h2>
          <p className="mt-8 text-[#525860] leading-relaxed max-w-md">
            Escolha o serviço e preencha o que souber. Nome, WhatsApp e região já bastam para entrarmos em contato — os demais campos ajudam a preparar um orçamento mais preciso.
          </p>
          <a href={waLink()} target="_blank" rel="noreferrer" data-testid="quote-whatsapp-link"
            className="inline-block mt-10 font-serif-d text-3xl md:text-4xl border-b-2 border-[#C59B27] hover:text-[#0F4C3A] transition-colors">11 99457-6696</a>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-7">
          {sent ? <Success sent={sent} onNew={() => setSent(null)} /> : (
            <form onSubmit={submit} data-testid="quote-form" className="bg-white p-6 sm:p-10 md:p-12 shadow-[0_30px_80px_-30px_rgba(18,21,24,0.18)] space-y-8">
              <ServiceToggle value={service} onChange={setService} />
              <div className="grid sm:grid-cols-2 gap-8">
                <Input label="Nome" k="name" f={f} set={set} required minLength={2} testid="input-name" placeholder="Seu nome" />
                <Input label="Telefone / WhatsApp" k="phone" f={f} set={set} required minLength={8} testid="input-phone" placeholder="(11) 90000-0000" />
              </div>
              <Input label="Bairro ou região" k="location" f={f} set={set} required minLength={2} testid="input-location" placeholder="Ex: Pinheiros, São Paulo" />
              {service === "comercial" ? <CommercialFields f={f} set={set} visit={visit} setVisit={setVisit} /> : <OtherFields service={service} f={f} set={set} />}
              <label className="block"><span className={lbl}>Descreva suas necessidades<span className="normal-case tracking-normal text-[#525860] ml-1">{OPT}</span></span>
                <textarea rows={3} data-testid="input-message" className={`${field} resize-none`} value={f.message} onChange={set("message")} placeholder="Tarefas, horários, materiais ou outras informações" /></label>
              <button disabled={loading} data-testid="btn-submit-quote"
                className="w-full rounded-full bg-[#121518] text-[#FAF8F5] py-4 font-medium flex items-center justify-center gap-2 hover:bg-[#0F4C3A] transition-colors disabled:opacity-60">
                {loading && <Loader2 size={18} className="animate-spin" />} Enviar pedido de orçamento
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};
